import React, { useEffect, useState } from 'react';
import { Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useHashState } from 'utils/hashState';
import { DSPPricingSchemeApplyFilterInitialValue } from './config/filterOptions';
import { DSPPricingApplyColumns } from './DSPPricingApplyColumns';
import { useDspPricingSchemeApplyQuery } from './hooks/useDspPricingSchemeApplyQuery';
import DSPPricingApplyFilter from './DSPPricingApplyFilter';
import DSPApplySchemeComp from './DSPApplySchemeComp';
import TablePaginationBar from '../../../../../../components/TablePaginationBar';
import { LIST_PRICING_SCHEME_APPLY_OPERATOR_OPTIONS, ListOperatorOptionsResponse } from '../graphql/queries/listOperatorOptions';
import { LIST_DSP_BIND_VALUE_OPTIONS, ListDspBindValueOptionsResponse } from './graphql/queries/listDspBindValueOptions';

interface DSPPricingApplyTabProps {
    businessScenario: number | null;
}

const DSPPricingApplyPage: React.FC<DSPPricingApplyTabProps> = ({ businessScenario }) => {
    const { t } = useTranslation();

    const [tempFilters, setTempFilters] = useHashState(DSPPricingSchemeApplyFilterInitialValue);
    const [filters, setFilters] = useHashState(DSPPricingSchemeApplyFilterInitialValue);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const {
        pricingSchemeApplyData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useDspPricingSchemeApplyQuery(filters, businessScenario, currentPage, pageSize);

    const {
        data: operatorOptionsData,
        loading: operatorOptionsLoading,
        refetch: refetchOperatorOptions
    } = useQuery<ListOperatorOptionsResponse>(
        LIST_PRICING_SCHEME_APPLY_OPERATOR_OPTIONS,
        { variables: { businessScenario: businessScenario ?? undefined } }
    );
    const operatorOptions = operatorOptionsData?.listPricingSchemeApplyOperatorOptions || [];

    const {
        data: dspBindValueOptionsData,
        loading: dspBindValueOptionsLoading,
        refetch: refetchDspBindValueOptions
    } = useQuery<ListDspBindValueOptionsResponse>(LIST_DSP_BIND_VALUE_OPTIONS);
    const dspBindValueOptions = dspBindValueOptionsData?.listDspBindValueOptions || [];

    const handleResetFilters = () => {
        setTempFilters(DSPPricingSchemeApplyFilterInitialValue);
        setFilters(DSPPricingSchemeApplyFilterInitialValue);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    useEffect(() => {
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize]);

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const [bindDrawerOpen, setBindDrawerOpen] = useState(false);
    const showBindDrawer = () => {
        setBindDrawerOpen(true);
    };
    const onCloseBindDrawer = () => {
        setBindDrawerOpen(false);
    };

    const [unBindDrawerOpen, setUnBindDrawerOpen] = useState(false);
    const showUnBindDrawer = () => {
        setUnBindDrawerOpen(true);
    };
    const onCloseUnBindDrawer = () => {
        setUnBindDrawerOpen(false);
    };

    const [bindEditDrawerOpen, setBindEditDrawerOpen] = useState(false);
    const showBindEditDrawer = () => {
        setBindEditDrawerOpen(true);
    };
    const onCloseBindEditDrawer = () => {
        setBindEditDrawerOpen(false);
    };

    const handleRefreshList = () => {
        refetch(buildQueryVariables());
        refetchOperatorOptions();
        refetchDspBindValueOptions();
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <DSPPricingApplyFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                handleFilter={handleFilter}
                handleResetFilters={handleResetFilters}
                showBindDrawer={showBindDrawer}
                showUnBindDrawer={showUnBindDrawer}
                showBindEditDrawer={showBindEditDrawer}
                operatorOptions={operatorOptions}
                operatorOptionsLoading={operatorOptionsLoading}
                dspBindValueOptions={dspBindValueOptions}
                dspBindValueOptionsLoading={dspBindValueOptionsLoading}
            />

            <DSPPricingApplyColumns
                rowData={pricingSchemeApplyData}
                loading={loading}
            />

            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
            />

            <Drawer
                title="方案绑定/Apply Price"
                width={800}
                onClose={onCloseBindDrawer}
                open={bindDrawerOpen}
                destroyOnClose={true}
                keyboard={false}
                mask={true}
                maskClosable={false}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
            >
                {bindDrawerOpen && (
                    <DSPApplySchemeComp
                        businessScenario={businessScenario}
                        mode="bind"
                        onClose={onCloseBindDrawer}
                        onSuccess={handleRefreshList}
                    />
                )}
            </Drawer>
            <Drawer
                title="方案解绑"
                width={800}
                onClose={onCloseUnBindDrawer}
                open={unBindDrawerOpen}
                destroyOnClose={true}
                keyboard={false}
                mask={true}
                maskClosable={false}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
            >
                {unBindDrawerOpen && (
                    <DSPApplySchemeComp
                        businessScenario={businessScenario}
                        mode="unbind"
                        onClose={onCloseUnBindDrawer}
                        onSuccess={handleRefreshList}
                    />
                )}
            </Drawer>

            <Drawer
                title="绑定编辑（追加绑定）"
                width={800}
                onClose={onCloseBindEditDrawer}
                open={bindEditDrawerOpen}
                destroyOnClose={true}
                keyboard={false}
                mask={true}
                maskClosable={false}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
            >
                {bindEditDrawerOpen && (
                    <DSPApplySchemeComp
                        businessScenario={businessScenario}
                        mode="bindEdit"
                        onClose={onCloseBindEditDrawer}
                        onSuccess={handleRefreshList}
                    />
                )}
            </Drawer>
        </div>
    );
};

export default DSPPricingApplyPage;
